/**
 * Het speelveld.
 *
 * De opzet volgt het origineel: beide vorstendommen liggen in dezelfde wereld,
 * gescheiden door de rivier. Dat van je tegenstander ligt boven, dat van jou onder. Je
 * schuift ertussen door met de muis, met F1 en F2, of de camera doet het zelf bij een
 * beurtwissel — precies wat het origineel met zijn scrollrand deed, maar dan vloeiend.
 *
 * De meetkunde komt uit de regelkernel: bouwwerken op oneven kolommen, landschappen op
 * even, boven en onder de bouwwerkenrij. Zie `packages/rules/src/state.ts`.
 */
import { BASIC_CARDS, LANDSCAPE_RESOURCE, type Seat } from '@catan/contracts';
import { slotsPerSide, type GameView } from '@catan/rules';
import {
  Application,
  Assets,
  Container,
  Graphics,
  Sprite,
  Text,
  TextStyle,
  Texture,
  TilingSprite,
} from 'pixi.js';
import { asset, assetGroup, boardImages, hasAsset, klik } from './assets.js';
import { dobbelworp } from './animaties.js';
import { Tooltips, volgTooltip } from './tooltip.js';
import { RASTER_BREED, RASTER_HOOG } from './skin.js';

/** De naam bij een kaartnummer — voor de naamplaatjes op de tegels. */
const kaartNamen = new Map(BASIC_CARDS.map((c) => [c.id, c.name]));
const kaartNaam = (id: number): string => kaartNamen.get(id) ?? `kaart ${id}`;

/** Een eenvoudige, stabiele hash van een roosterpositie — voor het strooien van groen. */
function hash(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  return Math.abs(h ^ (h >> 16));
}

/**
 * Maat van één cel op het bord.
 *
 * De originele platen zijn precies 100×100 en het origineel tekent ze op ware grootte,
 * met een haarlijn ertussen — de tegels raken elkaar bijna (opgemeten in
 * `catan-ref/shots/ref/18-na-volgende.png`: kolommen op 300, 401, 502, …). Ze oprekken
 * naar 104 maakte alles 4% te groot en gaf de rand een vage zoom.
 */
const CEL = 100;
/** Ruimte tussen de cellen — één pixel, zoals in het origineel. */
const KIER = 1;
/**
 * Verticale afstand tussen de twee vorstendommen.
 *
 * De rivier bestaat uit drie rijen tegels van 64 pixels: de noordoever met rotsen,
 * open water, en de zuidoever met schuim — dezelfde tegels die de speeltafel van het
 * origineel gebruikte.
 */
const OEVER_TEGEL = 64;
/**
 * Twee oeverrijen, geen aparte waterrij ertussen.
 *
 * Elke oevertegel bevat zelf al land én water; stapel je er ook nog een waterrij
 * tussen, dan wordt de rivier twee keer zo breed als in het origineel. Opgemeten op de
 * schermafdruk: daar is het open water 68 pixels, en dat is precies wat deze twee
 * rijen samen opleveren.
 */
const RIVIER_HOOGTE = OEVER_TEGEL * 2;

/**
 * Ruimte tussen het vorstendom van de tegenstander en de rivier.
 *
 * Zonder die ruimte piept zijn onderste rij landschappen onder de bovenste statusbalk
 * uit zodra je naar je eigen kant kijkt. In het origineel zie je daar alleen water en
 * oever — de overkant ligt buiten beeld tot je ernaartoe zwenkt.
 */
const OVERLOOP = 96;

/** Een vorstendom is drie rijen hoog: landschappen, bouwwerken, landschappen. */
const RIJEN = 3;
/**
 * Hoeveel rijen een vorstendom naar buiten kan uitgroeien.
 *
 * Een dorp draagt één uitbouwkaart per kant en die past in het vak naast het bouwwerk.
 * Een **stad** draagt er twee, en de tweede staat een rij verder naar buiten — boven de
 * bovenste rij, onder de onderste. Daar moet dus ruimte voor zijn, anders schuift die
 * kaart de rivier in.
 */
const UITGROEI_RIJEN = 1;

export interface BoardCallbacks {
  /** Aangeklikt landschap, in de coördinaten van de regelkernel. */
  onLandscapeClick(seat: Seat, column: number, side: 'boven' | 'onder'): void;
  /** Aangeklikte bouwplaats voor een straat of dorp. */
  onSpotClick(kind: 'straat' | 'dorp', column: number): void;
  /** Aangeklikt bouwwerk (om er een stad van te maken). */
  onBuildingClick(column: number): void;
  /** Aangeklikte vrije plek naast een bouwwerk, voor de uitbouwkaart in je hand. */
  onExpansionSlotClick(column: number, side: 'boven' | 'onder'): void;
  /**
   * Aangeklikte gebóuwde kaart — bij de Burgeroorlog wijs je er een aan bij je
   * tegenstander, om terug op zijn hand te laten nemen.
   */
  onGebouwdeKaartClick(column: number, side: 'boven' | 'onder'): void;
}

/** Wat het bord mag laten oplichten als mogelijke zet. */
export interface Highlights {
  readonly roadSpots: readonly number[];
  readonly settlementSpots: readonly number[];
  readonly upgradable: readonly number[];
  /**
   * De vrije plekken waar de gekozen uitbouwkaart bij kan — per bouwwerk én zijde.
   *
   * Het is niet genoeg om te weten wélk bouwwerk het mag zijn: een stad heeft boven en
   * onder allebei ruimte voor twee kaarten, en een kerk of raadhuis mag alleen bij een
   * stad. Daarom de kant erbij, zodat het bord precies het lege vak kan laten knipperen.
   */
  readonly expansionSpots?: readonly {
    readonly column: number;
    readonly side: 'boven' | 'onder';
  }[];
  /**
   * Nog lege landschapsplekken, tijdens de opzet.
   *
   * Het origineel tekent daar een zandkleurig vak (zie `kerk-bouwen.png`): je ziet
   * meteen waar er nog iets moet komen.
   */
  readonly emptyLandscapes?: readonly { readonly column: number; readonly side: string }[];
  /**
   * Grondstoffen die je al hebt aangeklikt maar die nog niet zijn afgerekend.
   *
   * Zolang je betaalt is de zet nog niet gedaan, dus in de spelstand liggen ze er nog.
   * Het bord trekt ze hier alvast af, zodat je meteen ziet dat je ze kwijt bent — anders
   * klik je in het duister en weet je pas achteraf of je genoeg had.
   */
  readonly betaald?: readonly { readonly column: number; readonly side: string }[];
  /** Landschappen die nu aangeklikt moeten worden, en bij wie. */
  readonly pickTiles?: { readonly seat: Seat; readonly tiles: readonly { column: number; side: string }[] };
  /**
   * Gebóuwde kaarten die nu aangeklikt moeten worden, en bij wie.
   *
   * Voor de Burgeroorlog: je wijst een ridder of vloot van je tegenstander aan die hij
   * terug op zijn hand moet nemen. Anders dan `expansionSpots` gaat dit dus over kaarten
   * die er al liggen, en over het vorstendom van de ánder.
   *
   * Zonder dit viel die vraag terug op de algemene landschappen-tak: dan lichtten je eigen
   * grondstofvelden op terwijl de kern een ridder van de overkant verwachtte, en liep het
   * potje vast.
   */
  readonly pickBuilt?: {
    readonly seat: Seat;
    readonly plekken: readonly { column: number; side: 'boven' | 'onder' }[];
  };
}

export class Board {
  private app: Application | undefined;
  /**
   * Alles wordt getekend op het raster van 1024×768 waarop het origineel tekende, en
   * daarna als geheel geschaald. Zo staat elk onderdeel op precies dezelfde plek als
   * toen, op elk scherm.
   */
  private wortel = new Container();
  private wereld = new Container();
  private mijnKant = new Container();
  private hunKant = new Container();
  /** Losse laag bovenop de wereld voor animaties; die beweegt niet mee met de camera. */
  private animatieLaag = new Container();
  /**
   * De laag bóven de omlijsting.
   *
   * Een kaart die groot in beeld komt en een grondstof die wegvliegt horen over de
   * statusbalken heen te liggen. `animatieLaag` zit onder de omlijsting en is daar dus
   * niet geschikt voor.
   */
  private bovenLaag = new Container();
  /** De zwevende toelichting; hangt boven alles, ook boven de animaties. */
  readonly tooltips = new Tooltips();
  private cameraDoel = 0;
  private cameraNu = 0;
  /**
   * De horizontale camera, voor als het vorstendom breder wordt dan het venster.
   *
   * Het origineel krimpt het veld niet: je kijkt opzij door de muis naar de linker- of
   * rechterrand te brengen, en het veld glijdt die kant op. `panOverschot` is hoeveel er
   * buiten beeld valt; 0 betekent alles past en de camera staat stil.
   */
  private cameraXDoel = 0;
  private cameraXNu = 0;
  private panRichting = 0;
  private panOverschot = 0;
  private basisX = 0;
  /** Schaalfactor; sinds de zijwaartse camera alleen nog door de hoogte begrensd. */
  private schaal = 1;
  private laatsteBlik: 'jij' | 'tegenstander' = 'jij';
  private callbacks: BoardCallbacks;
  private laatsteView: GameView | undefined;
  private laatsteHighlights: Highlights | undefined;
  /**
   * De vakken die knipperen.
   *
   * Ze worden bij elke tekening opnieuw gevuld; de ticker laat ze samen ademen. Eén
   * gedeelde hartslag, zodat ze in de maat lopen in plaats van door elkaar heen.
   */
  private knipperaars: Graphics[] = [];
  /** Loopt door zolang er iets knippert; bepaalt waar in de ademhaling we zitten. */
  private hartslag = 0;

  constructor(callbacks: BoardCallbacks) {
    this.callbacks = callbacks;
  }

  async mount(host: HTMLElement): Promise<void> {
    const app = new Application();
    await app.init({
      background: '#3c6b2f',
      resizeTo: host,
      antialias: false,
      // De platen zijn pixelkunst; nabijgelegen schaling houdt ze scherp.
      roundPixels: true,
      preference: 'webgl',
    });
    host.appendChild(app.canvas);
    this.app = app;

    await Assets.load(boardImages());

    app.stage.addChild(this.wortel);
    this.wortel.addChild(this.wereld, this.animatieLaag);
    this.wereld.addChild(this.hunKant, this.mijnKant);
    this.tekenAchtergrond();
    this.pasRasterAan();
    app.renderer.on('resize', () => this.pasRasterAan());

    // Camera glijdt naar zijn doel in plaats van te springen.
    app.ticker.add(() => {
      const verschil = this.cameraDoel - this.cameraNu;
      if (Math.abs(verschil) > 0.5) {
        this.cameraNu += verschil * 0.12;
        this.wereld.y = -this.cameraNu;
      }
      // Zijwaarts kijken: zolang de muis bij een rand hangt schuift het doel op, en de
      // camera glijdt er met dezelfde soepelheid achteraan als de verticale.
      if (this.panRichting !== 0 && this.panOverschot > 0) {
        this.cameraXDoel = Math.max(
          0,
          Math.min(this.panOverschot, this.cameraXDoel + this.panRichting * 0.45 * app.ticker.deltaMS),
        );
      }
      const opzij = this.cameraXDoel - this.cameraXNu;
      if (Math.abs(opzij) > 0.5) {
        this.cameraXNu += opzij * 0.15;
        this.wereld.x = this.basisX - this.cameraXNu;
      }
      // En de vrije plekken ademen, zodat ze opvallen zonder te schreeuwen.
      if (this.knipperaars.length > 0) {
        this.hartslag += app.ticker.deltaMS;
        const alfa = 0.45 + 0.35 * Math.sin(this.hartslag / 190);
        for (const vak of this.knipperaars) vak.alpha = alfa;
      }
    });

    // Met de muis naar de bovenrand kijk je bij de tegenstander — zoals in het origineel.
    // En bij de linker- of rechterrand glijdt het veld opzij, voor een vorstendom dat
    // breder is dan het venster.
    host.addEventListener('mousemove', (event) => {
      const rect = host.getBoundingClientRect();
      const y = event.clientY - rect.top;
      if (y < 60) this.kijkNaar('tegenstander');
      else if (y > rect.height - 60) this.kijkNaar('jij');
      const x = event.clientX - rect.left;
      this.panRichting = x < 60 ? -1 : x > rect.width - 60 ? 1 : 0;
    });
    host.addEventListener('mouseleave', () => {
      this.panRichting = 0;
    });

    window.addEventListener('keydown', (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        this.kijkNaar('jij');
      }
      if (event.key === 'F2') {
        event.preventDefault();
        this.kijkNaar('tegenstander');
      }
    });
  }

  /** Hangt de omlijsting in het vaste raster, bovenop het bord. */
  zetHud(laag: Container, vensters?: Container): void {
    this.wortel.addChild(laag);
    // Vensters staan boven de HUD maar onder de animaties.
    if (vensters !== undefined) this.wortel.addChild(vensters);
    // En daar weer bovenop de animaties, zodat die nergens achter verdwijnen.
    this.wortel.addChild(this.bovenLaag);
    // De toelichting hoort helemaal bovenaan: hij hoort nooit half achter iets te vallen.
    this.wortel.addChild(this.tooltips.laag);
  }

  /** De laag waarin de animaties tekenen (zie `animaties.ts`). */
  get animaties(): Container {
    return this.bovenLaag;
  }

  /**
   * Past het raster van 1024×768 in het venster.
   *
   * Bij gehele factoren blijft pixelkunst het scherpst, dus daar geven we de voorkeur
   * aan zolang dat niet te veel ruimte weggooit.
   */
  private pasRasterAan(): void {
    const app = this.app;
    if (app === undefined) return;
    const ruw = Math.min(app.screen.width / RASTER_BREED, app.screen.height / RASTER_HOOG);
    // Een hele factor is scherper; nemen we alleen als hij dicht genoeg in de buurt ligt.
    const heel = Math.floor(ruw);
    const factor = heel >= 1 && ruw - heel < 0.12 ? heel : ruw;
    this.wortel.scale.set(factor);
    this.wortel.x = (app.screen.width - RASTER_BREED * factor) / 2;
    this.wortel.y = (app.screen.height - RASTER_HOOG * factor) / 2;
  }

  /**
   * Zwenkt naar een van de twee vorstendommen en zet het midden in beeld.
   *
   * Een vorstendom is drie cellen hoog; de rest van de hoogte verdelen we erboven en
   * eronder, zodat de rivier net aan de rand meekomt.
   */
  kijkNaar(wie: 'jij' | 'tegenstander'): void {
    this.laatsteBlik = wie;
    // Het speelvenster is de ruimte tussen de statusbalken.
    const venster = { top: 78, hoogte: RASTER_HOOG - 78 - 52 };
    const veldHoogte = 3 * (CEL + KIER) * this.schaal;
    const marge = Math.max(0, (venster.hoogte - veldHoogte) / 2) + venster.top;
    const top = (wie === 'jij' ? this.hoogteVanHunKant() : 0) * this.schaal;
    this.cameraDoel = top - marge;
  }

  /** Waar de rivier begint: onder het vorstendom van de tegenstander, met zijn uitgroei. */
  private rivierTop(): number {
    return (RIJEN + UITGROEI_RIJEN) * (CEL + KIER) + OVERLOOP;
  }

  private hoogteVanHunKant(): number {
    // En onder de rivier nog een rij, want jouw steden groeien naar de rivier toe uit.
    return this.rivierTop() + RIVIER_HOOGTE + UITGROEI_RIJEN * (CEL + KIER);
  }

  private tekenAchtergrond(): void {
    const app = this.app;
    if (app === undefined) return;
    const gras = Texture.from(asset('achtergrond', 'gras').src);
    const veld = new TilingSprite({ texture: gras, width: 6000, height: 3000 });
    veld.y = -1000;
    veld.x = -2000;
    // In de wereld, niet op het toneel: zo schuift het gras mee met de camera en blijft
    // het beeld samenhangend.
    this.wereld.addChildAt(veld, 0);

    // De rivier tussen de twee vorstendommen, met de oevertegels van de speeltafel.
    // Eén tegel per oever, doorlopend herhaald.
    //
    // Afwisselen tussen twee varianten leek een goed idee, maar hun rotswanden staan
    // niet op dezelfde hoogte: dan krijg je zichtbare trapjes langs de oever. Het
    // origineel herhaalt ook gewoon; de tegel sluit naadloos op zichzelf aan.
    const rivier = new Container();
    const top = this.rivierTop();
    for (const [naam, offset] of [
      ['oever-noord', 0],
      ['oever-zuid', OEVER_TEGEL],
    ] as const) {
      const rij = new TilingSprite({
        texture: Texture.from(asset('rivier', naam).src),
        width: 6000,
        height: OEVER_TEGEL,
      });
      rij.x = -2000;
      rij.y = top + offset;
      rivier.addChild(rij);
    }
    // De brug over de rivier, in het midden van het speelveld — net als in het
    // origineel, waar hij boven de straat tussen de twee startdorpen ligt.
    const brugX = 2 * (CEL + KIER) + (CEL - 64) / 2;
    for (const [naam, offset] of [
      ['brug-noord', 0],
      ['brug-zuid', OEVER_TEGEL],
    ] as const) {
      const deel = new Sprite(Texture.from(asset('rivier', naam).src));
      deel.x = brugX;
      deel.y = top + offset;
      rivier.addChild(deel);
    }

    this.wereld.addChildAt(rivier, 1);
    this.strooiDecoratie();
  }

  /**
   * Strooit bomen, struiken en stenen over het gras.
   *
   * Het origineel had een tafel vol losse decoraties; een egale grasmat verraadt meteen
   * dat het niet het echte spel is. De plaatsing is deterministisch (een simpele hash
   * van de positie), zodat het beeld bij elke tekening hetzelfde blijft en
   * schermafdrukken vergelijkbaar zijn.
   */
  private strooiDecoratie(): void {
    const namen = Object.keys(assetGroup('decoratie'));
    if (namen.length === 0) return;
    const laag = new Container();

    // Een raster van 128 pixels; per vak bepaalt de hash of en waar er iets komt.
    const stap = 128;
    for (let gx = -12; gx < 24; gx++) {
      for (let gy = -6; gy < 14; gy++) {
        const h = hash(gx, gy);
        // Ongeveer twee op de vijf vakken krijgen iets: dicht genoeg om te leven,
        // ijl genoeg om het bord leesbaar te houden.
        if (h % 100 >= 38) continue;

        const x = gx * stap + (h % 71);
        const y = gy * stap + ((h >> 7) % 71);
        // De rivier vrijhouden: daar hoort geen boom te staan.
        const rivierTop = this.rivierTop();
        if (y > rivierTop - 60 && y < rivierTop + RIVIER_HOOGTE + 10) continue;

        const naam = namen[(h >> 3) % namen.length]!;
        const beeld = new Sprite(Texture.from(asset('decoratie', naam).src));
        // Op de speeltafel van het origineel zijn dit kleine pollen en struikjes van
        // twintig à veertig pixels, geen bomen die om aandacht vragen (vergelijk
        // `catan-ref/shots/ref/18-na-volgende.png`). Op ware grootte getekend werd het
        // gras een bos en verloor het bord zijn rust.
        const maat = naam.startsWith('boom') ? 40 : 30;
        beeld.setSize(maat, maat);
        beeld.x = x;
        beeld.y = y;
        beeld.alpha = 0.85;
        laag.addChild(beeld);
      }
    }
    this.wereld.addChildAt(laag, 1);
  }

  /** Tekent de hele stand opnieuw. */
  render(view: GameView, highlights: Highlights): void {
    if (this.app === undefined) return;
    this.laatsteView = view;
    this.laatsteHighlights = highlights;

    this.hunKant.removeChildren();
    this.mijnKant.removeChildren();
    // De vakken van de vorige tekening bestaan niet meer; anders houdt de ticker
    // weggegooide graphics vast.
    this.knipperaars = [];
    // De toelichting hoort bij wat er stond; na een hertekening klopt hij niet meer.
    this.tooltips.verberg();

    this.tekenVorstendom(this.hunKant, view.opponent.principality, view.opponent.seat, 0, {
      ...highlights,
      roadSpots: [],
      settlementSpots: [],
      upgradable: [],
    });
    this.tekenVorstendom(
      this.mijnKant,
      view.you.principality,
      view.you.seat,
      this.hoogteVanHunKant(),
      highlights,
    );

    this.centreer(view);
  }

  /**
   * Past het veld in het venster: schaalt zo nodig terug en centreert horizontaal.
   *
   * Een vorstendom groeit tijdens het spel naar beide kanten uit, en op een telefoon is
   * het scherm smal. In plaats van afsnijden schalen we het geheel tot het past — zo
   * blijft altijd alles zichtbaar.
   */
  private centreer(view: GameView): void {
    const app = this.app;
    if (app === undefined) return;

    const kolommen = [
      ...view.you.principality.landscapes.map((t) => t.column),
      ...view.you.principality.buildings.map((b) => b.column),
      ...view.opponent.principality.landscapes.map((t) => t.column),
      ...view.opponent.principality.buildings.map((b) => b.column),
      // En de plekken waar je nú kunt bouwen. Zonder deze viel de linkerkant buiten beeld:
      // een dorpplek ligt op `min − 2`, en die kolom kwam in deze telling niet voor. Rechts
      // hield het centreren vaak nog speling over, links niet — vandaar de klacht dat er
      // alleen aan de rechterkant een plek werd aangeboden. De regelkern bood ze allebei
      // aan; je kon er alleen niet bij.
      ...(this.laatsteHighlights?.roadSpots ?? []),
      ...(this.laatsteHighlights?.settlementSpots ?? []),
    ];
    const min = Math.min(...kolommen, 0);
    const max = Math.max(...kolommen, 4);
    const breedte = (max - min + 1) * (CEL + KIER);
    const hoogte = 3 * (CEL + KIER);

    // Het speelveld staat tussen de linkerstapels en de rechterkolom met handkaarten.
    const beschikbaar = RASTER_BREED - 60 - 70;
    // Niet meer krimpen om te passen: het origineel houdt de tegels op ware grootte en
    // laat je opzij kijken zodra het vorstendom breder wordt dan het venster ("de
    // dorpenkaarten worden kleiner" was de klacht). Alleen de hoogte blijft een grens,
    // en die raakt een vorstendom van drie cellen hoog nooit.
    this.schaal = Math.min(1, (RASTER_HOOG - 140) / hoogte);
    this.wereld.scale.set(this.schaal);
    const inhoud = breedte * this.schaal;
    const vorigOverschot = this.panOverschot;
    this.panOverschot = Math.max(0, inhoud - beschikbaar);
    this.basisX =
      60 + Math.max(0, (beschikbaar - inhoud) / 2) - min * (CEL + KIER) * this.schaal;
    // Wordt het veld nét te breed, begin dan in het midden — dan mist er aan beide
    // kanten evenveel en zie je meteen dat er opzij méér is.
    if (vorigOverschot === 0 && this.panOverschot > 0) {
      this.cameraXDoel = this.panOverschot / 2;
      this.cameraXNu = this.cameraXDoel;
    }
    this.cameraXDoel = Math.max(0, Math.min(this.panOverschot, this.cameraXDoel));
    this.cameraXNu = Math.max(0, Math.min(this.panOverschot, this.cameraXNu));
    this.wereld.x = this.basisX - this.cameraXNu;
    // De camera rekent in wereldcoördinaten; die moeten mee schalen.
    this.kijkNaar(this.laatsteBlik);
    this.wereld.y = -this.cameraNu;
  }

  /**
   * Waar staat dit landschap op het scherm?
   *
   * De vorstendommen tekenen in een geschaalde, verschoven wereld; de animatielaag
   * rekent in het vaste raster van 1024×768. Wie een schaap wil laten wegvliegen vanaf
   * de tegel die je aanklikte, heeft deze omrekening nodig.
   */
  schermPositie(seat: Seat, column: number, side: string): { x: number; y: number } {
    const eigen = this.laatsteView?.you.seat === seat;
    const yOffset = eigen ? this.hoogteVanHunKant() : 0;
    const rij = side === 'boven' ? 0 : side === 'midden' ? CEL + KIER : 2 * (CEL + KIER);
    return {
      x: this.wereld.x + (column * (CEL + KIER) + CEL / 2) * this.schaal,
      y: this.wereld.y + (yOffset + rij + CEL / 2) * this.schaal,
    };
  }

  private tekenVorstendom(
    laag: Container,
    principality: GameView['you']['principality'],
    seat: Seat,
    yOffset: number,
    highlights: Highlights,
  ): void {
    const rijY = (soort: 'boven' | 'midden' | 'onder'): number =>
      yOffset + (soort === 'boven' ? 0 : soort === 'midden' ? CEL + KIER : 2 * (CEL + KIER));

    /**
     * Waar de zoveelste uitbouwkaart aan deze kant komt te staan.
     *
     * De eerste ligt in het vak naast het bouwwerk, tussen de landschappen in. Elke
     * volgende staat een hele rij verder naar buiten — zo groeit een stad van het
     * bouwwerk af, precies zoals de kaarten op tafel liggen. Ze in één vak persen maakte
     * er twee halve kaartjes van.
     */
    const uitbouwY = (zijde: 'boven' | 'onder', index: number): number =>
      zijde === 'boven'
        ? rijY('boven') - index * (CEL + KIER)
        : rijY('onder') + index * (CEL + KIER);

    // Landschappen
    for (const tile of principality.landscapes) {
      const cel = this.tekenLandschap(tile, seat, highlights);
      cel.x = tile.column * (CEL + KIER);
      cel.y = rijY(tile.side);
      laag.addChild(cel);
    }

    // Straten en bouwwerken
    for (const road of principality.roads) {
      const sprite = this.plaat('bouwwerken', 'straat');
      sprite.x = road.column * (CEL + KIER);
      sprite.y = rijY('midden');
      volgTooltip(sprite, this.tooltips, 'Straat', () =>
        this.schermPositie(seat, road.column, 'midden'),
      );
      laag.addChild(sprite);
    }
    for (const building of principality.buildings) {
      const sprite = this.plaat('bouwwerken', building.kind);
      sprite.x = building.column * (CEL + KIER);
      sprite.y = rijY('midden');
      const uitbouwbaar =
        highlights.expansionSpots?.some((p) => p.column === building.column) === true;
      const aanwijsbaar = highlights.upgradable.includes(building.column) || uitbouwbaar;
      if (aanwijsbaar) {
        sprite.eventMode = 'static';
        sprite.cursor = 'pointer';
        sprite.on('pointertap', () => {
          klik();
          this.callbacks.onBuildingClick(building.column);
        });
      }
      laag.addChild(sprite);
      // De gloed hoort *over* het bouwwerk heen. Eronder zag je hem nooit: een dorp is
      // een dichte plaat van een hele cel, dus die dekte hem volledig af — en dan staat
      // er wel "klik op een oplichtend bouwwerk" terwijl er niets oplicht.
      if (aanwijsbaar) {
        // Goud voor "hier kan een stad", groen voor "hier past die uitbouwkaart".
        laag.addChild(this.gloed(sprite.x, sprite.y, uitbouwbaar ? 0x7bd44a : 0xffd257));
      }

      // "Dorp - 1 overwinningspunt", precies zoals het origineel het zegt.
      const punten = building.kind === 'stad' ? 2 : 1;
      const naam = building.kind === 'stad' ? 'Stad' : 'Dorp';
      volgTooltip(
        sprite,
        this.tooltips,
        `${naam} - ${punten} overwinningspunt${punten === 1 ? '' : 'en'}`,
        () => this.schermPositie(seat, building.column, 'midden'),
      );

      // Een gebouwde uitbouwkaart staat in het origineel als **de kaart zelf** in het
      // vrije vak boven of onder het bouwwerk: de plaat van de kaart met zijn naam in
      // het gekleurde balkje eronder (`catan-ref/shots/mij/kerk-bouwen.png` toont zo
      // "IJzergieterij" en "Steenoven"). Alleen een naambordje is te weinig — je wilt
      // zien wát er staat.
      for (const zijde of ['boven', 'onder'] as const) {
        const kaarten = building.expansions[zijde];
        kaarten.forEach((cardId, i) => {
          const plaatje = this.uitbouwKaart(cardId, CEL);
          plaatje.x = building.column * (CEL + KIER);
          plaatje.y = uitbouwY(zijde, i);
          volgTooltip(plaatje, this.tooltips, kaartNaam(cardId), () =>
            this.schermPositie(seat, building.column, zijde),
          );
          laag.addChild(plaatje);
        });
      }
    }

    // De gebouwde kaarten die je nu mag aanwijzen — bij de Burgeroorlog de ridders en
    // vloten van je tegenstander. Bewust ná het tekenen van de kaarten zelf, zodat de
    // markering er overheen ligt, en bewust zonder de "alleen mijn kant"-voorwaarde
    // hieronder: het hele punt is dat je in het vorstendom van de ánder aanwijst.
    if (highlights.pickBuilt !== undefined && highlights.pickBuilt.seat === seat) {
      for (const plek of highlights.pickBuilt.plekken) {
        const bouwwerk = principality.buildings.find((b) => b.column === plek.column);
        if (bouwwerk === undefined) continue;
        const aantal = bouwwerk.expansions[plek.side].length;
        for (let i = 0; i < aantal; i++) {
          const vak = new Graphics();
          vak.rect(0, 0, CEL, CEL).fill({ color: 0xd44a4a, alpha: 0.3 });
          vak.rect(2, 2, CEL - 4, CEL - 4).stroke({ color: 0xffd0d0, width: 3 });
          vak.x = plek.column * (CEL + KIER);
          vak.y = uitbouwY(plek.side, i);
          vak.eventMode = 'static';
          vak.cursor = 'pointer';
          vak.on('pointertap', () => {
            klik();
            this.callbacks.onGebouwdeKaartClick(plek.column, plek.side);
          });
          laag.addChild(vak);
          this.knipperaars.push(vak);
        }
      }
    }

    // De vrije plekken voor de uitbouwkaart die je in je hand hebt: knipperende vakken
    // boven en onder het bouwwerk, precies waar de kaart komt te staan. Een kerk of
    // raadhuis mag alleen bij een stad, dus daar knippert vanzelf niets bij een dorp —
    // de regelkernel geeft die plekken dan niet door.
    if (seat === this.laatsteView?.you.seat) {
      for (const plek of highlights.expansionSpots ?? []) {
        const bouwwerk = principality.buildings.find((b) => b.column === plek.column);
        if (bouwwerk === undefined) continue;
        // Een dorp heeft één plek per kant, een stad twee — en bij een lege stad horen ze
        // dus allebei te knipperen: het vak naast het bouwwerk én dat erboven of eronder.
        const bezet = bouwwerk.expansions[plek.side].length;
        const ruimte = slotsPerSide(bouwwerk.kind);
        for (let i = bezet; i < ruimte; i++) {
          const vak = new Graphics();
          vak.rect(0, 0, CEL, CEL).fill({ color: 0x7bd44a, alpha: 0.45 });
          vak.rect(2, 2, CEL - 4, CEL - 4).stroke({ color: 0xeaffd0, width: 3 });
          vak.x = plek.column * (CEL + KIER);
          vak.y = uitbouwY(plek.side, i);
          vak.eventMode = 'static';
          vak.cursor = 'pointer';
          vak.on('pointertap', () => {
            klik();
            this.callbacks.onExpansionSlotClick(plek.column, plek.side);
          });
          laag.addChild(vak);
          this.knipperaars.push(vak);
        }
      }
    }

    // Nog lege landschapsplekken tijdens de opzet: een zandkleurig vak, zoals het
    // origineel ze toont.
    for (const plek of highlights.emptyLandscapes ?? []) {
      const vak = new Container();
      vak.x = plek.column * (CEL + KIER);
      vak.y = rijY(plek.side as 'boven' | 'onder');
      const bodem = new Sprite(Texture.from(asset('skin', 'MENU_148').src));
      bodem.setSize(CEL, CEL);
      vak.addChild(bodem);
      const rand = new Graphics();
      rand.rect(1, 1, CEL - 2, CEL - 2).stroke({ color: 0xffd257, width: 2 });
      vak.addChild(rand);
      vak.eventMode = 'static';
      vak.cursor = 'pointer';
      vak.on('pointertap', () => {
        klik();
        this.callbacks.onLandscapeClick(seat, plek.column, plek.side as 'boven' | 'onder');
      });
      volgTooltip(vak, this.tooltips, 'Vrije plaats — hier kun je een landschap leggen', () =>
        this.schermPositie(seat, plek.column, plek.side),
      );
      laag.addChild(vak);
    }

    // Bouwplaatsen die nu mogelijk zijn: het lege kaartvak uit de originele skin met
    // het bouwwerk er doorschijnend overheen, zodat je ziet wát er komt.
    for (const column of highlights.roadSpots) {
      laag.addChild(
        this.bouwplaats(column * (CEL + KIER), rijY('midden'), 'straat', () =>
          this.callbacks.onSpotClick('straat', column),
        ),
      );
    }
    for (const column of highlights.settlementSpots) {
      laag.addChild(
        this.bouwplaats(column * (CEL + KIER), rijY('midden'), 'dorp', () =>
          this.callbacks.onSpotClick('dorp', column),
        ),
      );
    }
  }

  private plaat(groep: string, naam: string): Sprite {
    const entry = asset(groep, naam);
    const sprite = new Sprite(Texture.from(entry.src));
    sprite.width = CEL;
    sprite.height = CEL;
    return sprite;
  }

  private tekenLandschap(
    tile: GameView['you']['principality']['landscapes'][number],
    seat: Seat,
    highlights: Highlights,
  ): Container {
    const cel = new Container();
    const plaat = this.plaat('landschappen', tile.landscape);
    cel.addChild(plaat);

    // Het opbrengstgetal is in het origineel geen cijfer maar een **dobbelsteen**, wit
    // met zwarte ogen, midden op de tegel (zie `catan-ref/shots/ref/18-na-volgende.png`).
    // Dat is meteen het duidelijkste verschil met een gewoon getal: je leest het bord
    // zoals je een worp leest.
    const steenNaam = `opbrengst-${tile.dieNumber}`;
    if (hasAsset('dobbelstenen', steenNaam)) {
      const steen = new Sprite(Texture.from(asset('dobbelstenen', steenNaam).src));
      steen.setSize(40, 41);
      steen.x = (CEL - 40) / 2;
      steen.y = (CEL - 41) / 2;
      cel.addChild(steen);
    } else {
      // Terugval voor een ontbrekend vlak; liever een cijfer dan niets.
      const getal = new Text({
        text: String(tile.dieNumber),
        style: new TextStyle({
          fontFamily: 'Georgia, serif',
          fontSize: 22,
          fontWeight: 'bold',
          fill: 0xffffff,
          stroke: { color: 0x2b1d0e, width: 4 },
        }),
      });
      getal.x = CEL - 26;
      getal.y = CEL - 32;
      cel.addChild(getal);
    }

    // De grondstoffen die erop liggen, als symbolen linksboven. Wat je al hebt
    // aangeklikt om mee te betalen, telt hier alvast niet meer mee.
    const soort = LANDSCAPE_RESOURCE[tile.landscape];
    const alBetaald =
      seat === this.laatsteView?.you.seat
        ? (highlights.betaald ?? []).filter(
            (p) => p.column === tile.column && p.side === tile.side,
          ).length
        : 0;
    const over = Math.max(0, tile.resources - alBetaald);
    if (over > 0) {
      const breedte = over * 24 + 6;
      const plankje = new Graphics();
      plankje
        .roundRect(3, 3, breedte, 30, 5)
        .fill({ color: 0x000000, alpha: 0.35 });
      cel.addChild(plankje);
      for (let i = 0; i < over; i++) {
        const symbool = new Sprite(Texture.from(asset('symbolen', soort).src));
        symbool.width = 22;
        symbool.height = 22;
        symbool.x = 6 + i * 24;
        symbool.y = 7;
        cel.addChild(symbool);
      }
    }

    const teKiezen =
      highlights.pickTiles?.seat === seat &&
      highlights.pickTiles.tiles.some((t) => t.column === tile.column && t.side === tile.side);
    if (teKiezen) {
      cel.addChild(this.gloed(0, 0, 0x7bd44a));
    }

    cel.eventMode = 'static';
    cel.cursor = 'pointer';
    cel.on('pointertap', () => {
      klik();
      this.callbacks.onLandscapeClick(seat, tile.column, tile.side);
    });

    // De toelichting die het origineel ook geeft: wat is dit, wat levert het op, en
    // hoeveel ligt er nu.
    const naam = tile.landscape.charAt(0).toUpperCase() + tile.landscape.slice(1);
    volgTooltip(
      cel,
      this.tooltips,
      () => `${naam} — ${soort} bij een ${tile.dieNumber} (${tile.resources} in voorraad)`,
      () => this.schermPositie(seat, tile.column, tile.side),
    );
    return cel;
  }

  /**
   * Een gebouwde uitbouwkaart op zijn plek: de kaartplaat met de naam in het balkje.
   *
   * De kaartplaten van 400×400 dragen hun gekleurde balkje al ingebakken — groen voor
   * wat bij dorp én stad mag, roze voor wat alleen bij een stad past. De naam tekent het
   * spel eroverheen; die zit niet in het plaatje. Ontbreekt de plaat, dan valt hij terug
   * op het losse naambordje.
   */
  private uitbouwKaart(cardId: number, hoog: number): Container {
    if (!hasAsset('kaarten', String(cardId))) {
      const los = this.naamplaatje(cardId);
      los.x = 4;
      los.y = hoog - 20;
      const doos = new Container();
      doos.addChild(los);
      return doos;
    }
    const doos = new Container();
    const plaat = new Sprite(Texture.from(asset('kaarten', String(cardId)).src));
    plaat.setSize(CEL, hoog);
    doos.addChild(plaat);

    // Het balkje zit op 65,5%–97,5% van de plaathoogte; de naam gaat in het midden.
    const label = new Text({
      text: kaartNaam(cardId),
      style: new TextStyle({
        fontFamily: 'Tahoma, "DejaVu Sans", sans-serif',
        fontSize: Math.max(7, Math.round(hoog * 0.11)),
        fill: 0x14260a,
      }),
    });
    label.anchor.set(0.5, 0.5);
    label.x = CEL / 2;
    label.y = hoog * 0.815;
    if (label.width > CEL - 24) label.scale.set((CEL - 24) / label.width);
    doos.addChild(label);
    return doos;
  }

  /**
   * Het groene naamplaatje van een gebouwde uitbouwkaart.
   *
   * Een smal groen balkje onderaan de tegel met de kaartnaam erin, en een driehoekje aan
   * beide kanten — het terugvalspoor als de kaartplaat ontbreekt.
   */
  private naamplaatje(cardId: number): Container {
    const doos = new Container();
    const breed = CEL - 8;
    const hoog = 18;

    const vlak = new Graphics();
    vlak
      .rect(0, 0, breed, hoog)
      .fill({ color: 0x7bd44a })
      .stroke({ color: 0x2f6d18, width: 2 });
    doos.addChild(vlak);

    const pijl = (x: number, kant: 1 | -1): void => {
      const g = new Graphics();
      g.poly([x, hoog / 2, x + kant * 6, 4, x + kant * 6, hoog - 4]).fill({ color: 0x2f6d18 });
      doos.addChild(g);
    };
    pijl(5, 1);
    pijl(breed - 5, -1);

    const naam = kaartNaam(cardId);
    const label = new Text({
      text: naam,
      style: new TextStyle({
        fontFamily: 'Tahoma, "DejaVu Sans", sans-serif',
        fontSize: 10,
        fill: 0x14260a,
      }),
    });
    label.anchor.set(0.5, 0.5);
    label.x = breed / 2;
    label.y = hoog / 2;
    // Past de naam niet, dan krimpen we hem liever dan dat hij over de rand loopt.
    if (label.width > breed - 20) label.scale.set((breed - 20) / label.width);
    doos.addChild(label);
    return doos;
  }

  /**
   * De markering "hier mag het".
   *
   * Het origineel legt een groene waas over de héle tegel, binnen zijn eigen rand — geen
   * halo eromheen (`catan-ref/shots/mij/kerk-bouwen.png`). Een gloed die buiten de tegel
   * uitsteekt loopt over de buurtegels heen en maakt het bord juist onrustiger.
   */
  /**
   * Het vlakje dat zegt "hier kun je klikken".
   *
   * Het hoort *over* het plaatje heen. Landschappen en bouwwerken zijn dichte platen van
   * een hele cel, dus eronder is hij simpelweg onzichtbaar. Daarom een lichte waas met
   * een duidelijke rand eromheen — en `eventMode = 'none'`, zodat de klik gewoon
   * doorgaat naar het plaatje eronder.
   */
  private gloed(x: number, y: number, kleur: number): Graphics {
    const g = new Graphics();
    g.rect(x, y, CEL, CEL).fill({ color: kleur, alpha: 0.28 });
    g.rect(x + 2, y + 2, CEL - 4, CEL - 4).stroke({ color: kleur, width: 3 });
    g.eventMode = 'none';
    return g;
  }

  private bouwplaats(x: number, y: number, wat: 'straat' | 'dorp', onClick: () => void): Container {
    const doos = new Container();

    // Het lege parkementvak uit de skin als bodem.
    const bodem = new Sprite(Texture.from(asset('skin', 'MENU_148').src));
    bodem.setSize(CEL, CEL);
    doos.addChild(bodem);

    // Het bouwwerk doorschijnend erover: zo zie je wat er komt te staan.
    const voorbeeld = this.plaat('bouwwerken', wat);
    voorbeeld.alpha = 0.45;
    doos.addChild(voorbeeld);

    const rand = new Graphics();
    rand.roundRect(1, 1, CEL - 2, CEL - 2, 6).stroke({ color: 0xffd257, width: 3 });
    doos.addChild(rand);

    doos.x = x;
    doos.y = y;
    doos.eventMode = 'static';
    doos.cursor = 'pointer';
    // Een zacht knipperend randje trekt de aandacht zonder te schreeuwen.
    doos.on('pointerover', () => (rand.alpha = 0.4));
    doos.on('pointerout', () => (rand.alpha = 1));
    doos.on('pointertap', () => {
      klik();
      onClick();
    });
    return doos;
  }

  /**
   * Laat de dobbelsteen rollen met de originele frames.
   *
   * De animatie is puur sfeer: de uitkomst staat al vast in de spelstand. Wie geen
   * beweging wil (`prefers-reduced-motion`) ziet alleen het eindbeeld.
   */
  /** De worp: beide stenen rollen en blijven leesbaar liggen (zie `animaties.ts`). */
  async dobbelAnimatie(opbrengst: number, gebeurtenis: string): Promise<void> {
    if (this.app === undefined) return;
    await dobbelworp(this.bovenLaag, opbrengst, gebeurtenis);
  }

  /** Een korte flits over een plek op het scherm — voor een gespeelde kaart. */
  flits(x: number, y: number): void {
    const frames = assetGroup('anim-effect-ster');
    const namen = Object.keys(frames).sort((a, b) => Number(a) - Number(b));
    if (namen.length === 0 || this.app === undefined) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const sprite = new Sprite(Texture.from(frames[namen[0]!]!.src));
    sprite.setSize(128, 128);
    sprite.x = x - 64;
    sprite.y = y - 64;
    this.animatieLaag.addChild(sprite);
    let frame = 0;
    const stap = (): void => {
      sprite.texture = Texture.from(frames[namen[Math.min(frame, namen.length - 1)]!]!.src);
      frame += 1;
      if (frame < namen.length) window.setTimeout(stap, 50);
      else sprite.destroy();
    };
    stap();
  }

  /** Opnieuw tekenen met de laatst bekende stand — na het wisselen van venstergrootte. */
  herteken(): void {
    if (this.laatsteView && this.laatsteHighlights) {
      this.render(this.laatsteView, this.laatsteHighlights);
    }
  }
}
